import * as signalR from "@microsoft/signalr";
import {
    DataFrame,
    IDataFrame,
    Method,
    Status,
} from "../DataFrame";
import {
    ConnectionResult,
    IExalusConnectionService,
    AuthorizationInfo,
    ConnectionState,
    StreamError,
    StreamHandler,
    BrokerInfo,
    BrokerEnvironment,
} from "./IExalusConnectionService";
import { ITypedEvent, TypedEvent } from "../TypedEvent";
import { Api } from "../Api";
import { LoggerService } from "./Logging/LoggerService";
import { ILoggerService } from "./Logging/ILoggerService";
import { IEvent, Event } from "../Event";
import {
    IControllerConfigurationService,
} from "./Controller/IControllerConfigurationService";
import { ControllerConfigurationService } from "./Controller/ControllerConfigurationService";
import { IWebApiCacheService } from "./WebApi/IWebApiCacheService";
import { WebApiCacheService } from "./WebApi/WebApiCacheService";
import { SessionService } from "./Session/SessionService";
import { LogLevel } from "./Logging/ILoggerService";
import { DependencyContainer } from "../DependencyContainer";
import { AppState, IAppStateService } from "./IAppStateService";
import { AppStateService } from "./AppStateService";

class FastRetryPolicy implements signalR.IRetryPolicy {
    nextRetryDelayInMilliseconds(context: signalR.RetryContext): number | null {
        const retryDelays = [0, 1_000, 3_000, 5_000, 10_000, 30_000];
        return retryDelays[Math.min(context.previousRetryCount, retryDelays.length - 1)];
    }
}

const TRANSPORTS: signalR.HttpTransportType[] = [
    signalR.HttpTransportType.WebSockets,
    signalR.HttpTransportType.ServerSentEvents,
    signalR.HttpTransportType.LongPolling,
];

export class SignalrLogger implements signalR.ILogger {
    private readonly _log: ILoggerService = Api.Get<ILoggerService>(LoggerService.ServiceName);
    log(level: signalR.LogLevel, message: string): void {
        if (level < ExalusConnectionService.SignalRLogLevel) return;
        message = `[SignalR] ${message}`;
        switch (level) {
            case signalR.LogLevel.Critical:
            case signalR.LogLevel.Error:
                this._log.Error(message);
                break;
            case signalR.LogLevel.Warning:
                this._log.Warning(message);
                break;
            case signalR.LogLevel.Information:
                this._log.Info(message);
                break;
            default:
                this._log.Debug(message);
                break;
        }
    }
}

export class ExalusConnectionService implements IExalusConnectionService {
    private static readonly PING_INTERVAL_MS = 5_000;
    private static readonly MAX_CONSECUTIVE_PING_FAILURES = 6;
    private static readonly SERVER_TIMOUT_MS = 10_000; // keep the original name
    public static SignalRLogLevel: signalR.LogLevel = signalR.LogLevel.Warning;

    private _connection?: signalR.HubConnection;
    private _connectTask: Promise<ConnectionResult> | null = null;
    private _connectTaskVersion: number | null = null;
    private _reconnectTask: Promise<ConnectionResult> | null = null;
    private _reconnectTaskVersion: number | null = null;
    private _restoreTask: Promise<boolean> | null = null;
    private _restoreTaskVersion: number | null = null;
    private _connectionValidationTask: Promise<boolean> | null = null;
    private _authorizeTask: Promise<boolean> | null = null;
    private _authorizeTaskVersion: number | null = null;
    private _authorizeTaskConnection?: signalR.HubConnection;
    private _authorizeTaskCancel?: () => void;
    private _pingTimerId: number | null = null;
    private _consecutivePingFailures = 0;
    private _disconnectedOnPurpose = false;
    private _lifecycleVersion = 0;

    private _address: string | undefined = "packets-broker1.tr7.pl";
    private _currentAddress?: string;
    private _serversBrokerAddress = "https://servers-broker.tr7.pl";
    private readonly _serversBrokerAddressList = [
        "https://servers-broker.tr7.pl",
        "https://broker.tr7.pl",
        "https://dev-broker.tr7.pl",
    ];
    private _allBrokersChecked = false;
    private readonly _packetsBrokerServers = [
        "packets-broker1.tr7.pl",
        "packets-broker2.tr7.pl"
    ];
    private _connectedServerAddressResult: BrokerInfo | null = null;
    private readonly _connectedServerAddressWaiters: Array<(brokerInfo: BrokerInfo) => void> = [];

    private _isAuthorized = false;
    private _serialId?: string;
    private _PIN?: string;

    private _lastReceivedPacket = Date.now();
    private _everConnected = false;

    private readonly _pendingRequests = new Map<
        string,
        { resolve: (v: IDataFrame<any> | void) => void; reject: (r: any) => void; timeoutId: number }
    >();

    private readonly _log: ILoggerService = Api.Get<ILoggerService>(LoggerService.ServiceName);
    private readonly _appState = Api.Get<IAppStateService>(AppStateService.ServiceName);
    private _controllerConfiguration: IControllerConfigurationService | null = null;
    private _cache: IWebApiCacheService | null = null;
    private _session: SessionService | null = null;

    private readonly _dataReceivedEvent = new TypedEvent<DataFrame<any>>();
    private readonly _pongReceivedEvent = new Event();
    private readonly _authorizationReceivedEvent = new TypedEvent<boolean>();
    private readonly _registrationReceivedEvent = new TypedEvent<string>();
    private readonly _streamStartedEvent = new TypedEvent<string>();
    private readonly _connectionStateChangedEvent = new TypedEvent<ConnectionState>();
    private readonly _errorOccuredEvent = new TypedEvent<[string, string]>();
    public static readonly ServiceName = "ExalusConnectionService";

    constructor() {
        this._appState.OnAppStateChanged().Subscribe((newState: AppState) => {
            void (async () => {
                this._log.Debug(ExalusConnectionService.ServiceName, `App state changed: ${newState}`);
                switch (newState) {
                    case AppState.ExitedLowPowerMode:
                    case AppState.ReturnedFromSuspension:
                        this._log.Debug(ExalusConnectionService.ServiceName, `Handling app state change: ${newState}`);
                        if (this.GetAuthorizationInfo() !== null && await this.RestoreConnectionAsync())
                            await this._session?.RestoreSessionAsync();
                        break;
                    case AppState.Connected:
                        this.resolveConnectedBrokerInfo(this._serversBrokerAddress, this._address);
                        break;
                }
            })().catch(error => {
                this._log.Warning(ExalusConnectionService.ServiceName, `App state handling failed: ${error}`);
            });
        });
    }

    GetServiceName(): string {
        return ExalusConnectionService.ServiceName;
    }

    GetAuthorizationInfo(): AuthorizationInfo | null {
        return this._serialId && this._PIN ? new AuthorizationInfo(this._serialId, this._PIN) : null;
    }

    GetControllerSerialNumber(): string | undefined {
        return this._serialId;
    }

    GetControllerPin(): string | undefined {
        return this._PIN;
    }

    SetServersBrokerAddress(a: string): void {
        this._serversBrokerAddress = a;
    }

    SetDefaultPacketsBrokerAddress(a: string): void {
        this._address = a;
    }

    async GetConnectedBrokerInfoAsync(timeoutMs: number | null = 8_000): Promise<BrokerInfo> {
        if (this._connectedServerAddressResult !== null){
            return this._connectedServerAddressResult;
        }

        return new Promise<BrokerInfo>((resolve, reject) => {
            let timeoutId: number | undefined;

            const waiter = (brokerInfo: BrokerInfo) => {
                if (timeoutId !== undefined)
                    window.clearTimeout(timeoutId);
                resolve(brokerInfo);
            };

            this._connectedServerAddressWaiters.push(waiter);

            if (timeoutMs === null)
                return;

            timeoutId = window.setTimeout(() => {
                const idx = this._connectedServerAddressWaiters.indexOf(waiter);
                if (idx >= 0)
                    this._connectedServerAddressWaiters.splice(idx, 1);
                reject(new Error("GetConnectedBrokerInfoAsync timed out"));
            }, timeoutMs);
        });
    }

    EnablePacketsLogging(): void {
        (window as any)["packets"] = true;
    }

    DisablePacketsLogging(): void {
        (window as any)["packets"] = false;
    }

    SubscribeTo<T>(resourceId: string, handler: (d: IDataFrame<T>) => void): () => void {
        const h = (f: IDataFrame<T>) => {
            if (f.Resource === resourceId) handler(f);
        };
        this._dataReceivedEvent.Subscribe(h);
        return () => this._dataReceivedEvent.Unsubscribe(h);
    }

    private isLifecycleCurrent(version: number): boolean {
        return version === this._lifecycleVersion && !this._disconnectedOnPurpose;
    }

    private beginExplicitConnection(): number {
        this._authorizeTaskCancel?.();
        this._lifecycleVersion++;
        this._disconnectedOnPurpose = false;
        return this._lifecycleVersion;
    }

    private async reauthorizeIfPossible(version = this._lifecycleVersion): Promise<boolean> {
        if (!this._serialId || !this._PIN) {
            this._isAuthorized = false;
            return false;
        }
        const ok = await this.authorizeAsyncCore(new AuthorizationInfo(this._serialId, this._PIN), version);
        if (this.isLifecycleCurrent(version))
            this._isAuthorized = ok;
        return ok;
    }

    async GetServerAddressAsync(): Promise<string | null> {
        if (!this._serialId) {
            this._log.Warning(
                ExalusConnectionService.ServiceName,
                "GetServerAddressAsync() – serialId not set"
            );
            return null;
        }

        const url = `${this._serversBrokerAddress}/api/connections/broker/whichserver/${this._serialId}`;

        try {
            const resp = await fetch(url);

            switch (resp.status) {
                case 200: {
                    const addr = (await resp.text()).trim();
                    if (addr) {
                        this._log.Debug(
                            ExalusConnectionService.ServiceName,
                            `Broker address resolved: ${addr}`
                        );
                        return addr;
                    }
                    this._log.Warning(
                        ExalusConnectionService.ServiceName,
                        "Broker returned empty address"
                    );
                    return null;
                }
                case 204: {
                    if (!this._allBrokersChecked) {
                        this._log.Warning(
                            ExalusConnectionService.ServiceName,
                            `Servers-broker ${this._serversBrokerAddress} returned 204 (no content) – controller not found on this server`
                        );

                        await this.rotateServersBroker();
                        return this.GetServerAddressAsync();
                    }
                    else {
                        this._log.Error(
                            ExalusConnectionService.ServiceName,
                            "All servers-brokers checked, controller not found – likely offline"
                        );
                        return null;
                    }
                }
                default: {
                    this._log.Warning(
                        ExalusConnectionService.ServiceName,
                        `GetServerAddressAsync() – HTTP ${resp.status}`
                    );
                    return null;
                }
            }
        } catch (err) {
            this._log.Error(ExalusConnectionService.ServiceName, String(err));
            return null;
        }
    }

    private async rotateServersBroker(): Promise<void> {
        const idx = this._serversBrokerAddressList.indexOf(this._serversBrokerAddress);
        const next = idx + 1 < this._serversBrokerAddressList.length ? idx + 1 : 0;

        this._serversBrokerAddress = this._serversBrokerAddressList[next];
        this._allBrokersChecked = next === 0;

        this._log.Info(
            ExalusConnectionService.ServiceName,
            `Switching servers-broker to: ${this._serversBrokerAddress}`
        );
    }

    async ConnectAsync(address: string): Promise<ConnectionResult> {
        const version = this.beginExplicitConnection();
        this._address = address;
        return this.serialisedConnect(version);
    }

    async ConnectAndAuthorizeAsync(info: AuthorizationInfo): Promise<ConnectionResult> {
        const version = this.beginExplicitConnection();
        return this.connectAndAuthorizeCore(info, version);
    }

    private async connectAndAuthorizeCore(info: AuthorizationInfo, version: number): Promise<ConnectionResult> {
        if (!this.isLifecycleCurrent(version))
            return ConnectionResult.FailedToConnect;

        this.resetConnectedServerAddressResolution();

        this._serialId = info.serialNumber;
        this._PIN = info.pin;
        Api.WorksInContextOf = this._serialId;

        // reset servers-broker rotation before each attempt
        this._allBrokersChecked = false;
        this._serversBrokerAddress = this._serversBrokerAddressList[0];

        const packetsBroker = await this.GetServerAddressAsync();
        if (!this.isLifecycleCurrent(version))
            return ConnectionResult.FailedToConnect;

        if (packetsBroker) {
            this.SetDefaultPacketsBrokerAddress(packetsBroker);

            const result = await this.connectAndEnsureAuthorized(info, version);
            if (result === ConnectionResult.Connected) {
                return ConnectionResult.Connected;
            }
        }

        for (const host of this._packetsBrokerServers) {
            if (!this.isLifecycleCurrent(version))
                return ConnectionResult.FailedToConnect;

            this._log.Info(ExalusConnectionService.ServiceName, `Fallback broker: ${host}`);
            this.SetDefaultPacketsBrokerAddress(host);

            const result = await this.connectAndEnsureAuthorized(info, version);
            if (result === ConnectionResult.Connected) {
                return ConnectionResult.Connected;
            }
        }

        // Failure to complete AuthorizeTo is a connection/reachability failure.
        // The controller has not rejected the user's login and credentials must
        // not be reported as invalid here.
        return ConnectionResult.FailedToConnect;
    }

    private async connectAndEnsureAuthorized(info: AuthorizationInfo, version: number): Promise<ConnectionResult> {
        if (!this.isLifecycleCurrent(version))
            return ConnectionResult.FailedToConnect;

        const connectionResult = await this.serialisedConnect(version);
        if (connectionResult !== ConnectionResult.Connected)
            return connectionResult;

        if (!this.isLifecycleCurrent(version))
            return ConnectionResult.FailedToConnect;

        return (this._isAuthorized || await this.authorizeAsyncCore(info, version))
            ? ConnectionResult.Connected
            : ConnectionResult.FailedToConnect;
    }

    private resetConnectedServerAddressResolution(): void {
        this._connectedServerAddressResult = null;
    }

    private resolveConnectedBrokerInfo(brokerAddress: string, controllerAddress: string | undefined): void {
        const isDev = this._serversBrokerAddress.includes('dev-broker.tr7.pl') && !this._packetsBrokerServers.any(s=>s.includes(this._currentAddress ?? ""));

        this._connectedServerAddressResult = new BrokerInfo(brokerAddress, controllerAddress, isDev ? BrokerEnvironment.Development : BrokerEnvironment.Production);
        while (this._connectedServerAddressWaiters.length > 0) {
            const waiter = this._connectedServerAddressWaiters.shift();
            waiter?.(this._connectedServerAddressResult);
        }
    }

    async AuthorizeAsync(info: AuthorizationInfo): Promise<boolean> {
        this._serialId = info.SerialNumber;
        this._PIN = info.PIN;
        Api.WorksInContextOf = this._serialId;
        return this.authorizeAsyncCore(info, this._lifecycleVersion);
    }

    private authorizeAsyncCore(info: AuthorizationInfo, version: number): Promise<boolean> {
        if (!this.isLifecycleCurrent(version))
            return Promise.resolve(false);

        const connection = this._connection;
        if (connection?.state !== signalR.HubConnectionState.Connected)
            return Promise.resolve(false);

        if (this._authorizeTask &&
            this._authorizeTaskVersion === version &&
            this._authorizeTaskConnection === connection)
            return this._authorizeTask;

        this._authorizeTaskCancel?.();

        let task: Promise<boolean>;
        let cancelAuthorization: (() => void) | undefined;
        const authorizationTask = new Promise<boolean>((resolve) => {
            let completed = false;
            let timeoutId: number | undefined;

            const complete = (ok: boolean) => {
                if (completed)
                    return;

                completed = true;
                if (timeoutId !== undefined)
                    window.clearTimeout(timeoutId);
                this._authorizationReceivedEvent.Unsubscribe(onAuth);
                const isCurrent = this.isLifecycleCurrent(version) && this._connection === connection;
                if (isCurrent)
                    this._isAuthorized = ok;

                if (isCurrent && ok)
                    this._connectionStateChangedEvent.Invoke(ConnectionState.ConnectedAndAuthorized);

                resolve(isCurrent && ok);
            };

            const onAuth = (ok: boolean) => {
                this._log.Debug(ExalusConnectionService.ServiceName, `Authorization -> ${ok}`);
                complete(ok);
            };

            timeoutId = window.setTimeout(() => {
                this._log.Debug(ExalusConnectionService.ServiceName, "AuthorizeAsync timed out");
                complete(false);
            }, 2_000);

            this._authorizationReceivedEvent.Subscribe(onAuth);
            cancelAuthorization = () => complete(false);
            this._log.Debug(ExalusConnectionService.ServiceName, `Authorizing to ${info.SerialNumber}...`);
            void connection.send("AuthorizeTo", info.SerialNumber, info.PIN).catch(error => {
                this._log.Warning(ExalusConnectionService.ServiceName, `Authorization request failed: ${error}`);
                complete(false);
            });
        });

        task = authorizationTask.finally(() => {
            if (this._authorizeTask === task) {
                this._authorizeTask = null;
                this._authorizeTaskVersion = null;
                this._authorizeTaskConnection = undefined;
                this._authorizeTaskCancel = undefined;
            }
        });

        this._authorizeTask = task;
        this._authorizeTaskVersion = version;
        this._authorizeTaskConnection = connection;
        this._authorizeTaskCancel = cancelAuthorization;

        return task;
    }

    IsConnected(): boolean {
        return this._connection?.state === signalR.HubConnectionState.Connected;
    }

    async DisconnectAsync(): Promise<void> {
        this._authorizeTaskCancel?.();
        this._lifecycleVersion++;
        this._disconnectedOnPurpose = true;
        this._isAuthorized = false;
        this._log.Debug(ExalusConnectionService.ServiceName, "Disconnecting...");
        try {
            await this._connection?.stop();
        } catch (error) {
            this._log.Warning(ExalusConnectionService.ServiceName, `Disconnect failed: ${error}`);
        }
        this.cleanup();
    }

    async SendAsync(dataFrame: IDataFrame<any>, logTx = false): Promise<boolean> {
        if (!this.IsConnected()) throw new Error("Not connected");

        const sendCore = async () => {
            await this._connection!.invoke("SendTo", this._serialId, dataFrame);
        };

        const dump = (window as any)["packets"] === true;
        if (dump || logTx) {
            this._log.Debug(
                ExalusConnectionService.ServiceName,
                `⇢ ${dataFrame.Resource} ${dataFrame.Method} ${dataFrame.TransactionId}` +
                (dump ? `\n${JSON.stringify(dataFrame, null, 2)}` : "")
            );
        }

        try {
            await sendCore();
            return true;
        } catch (err: any) {
            const msg = String(err?.message ?? err).toLowerCase();

            if (msg.includes("unauthorized")) {
                // 1) try to re-authorize on current connection
                const reauthOk = await this.reauthorizeIfPossible();

                // 2) if still unauthorized, re-resolve broker and reconnect
                if (!reauthOk) {
                    // reset broker rotation before resolving again
                    this._allBrokersChecked = false;
                    this._serversBrokerAddress = this._serversBrokerAddressList[0];

                    const addr = await this.GetServerAddressAsync();
                    if (addr && addr !== this._address) {
                        this._log.Info(ExalusConnectionService.ServiceName, `Switching to resolved broker: ${addr}`);
                        try { await this._connection?.stop(); } catch {}
                        this.cleanup();
                        await this.ConnectAsync(addr);
                        await this.reauthorizeIfPossible();
                    }
                }

                if (this.IsConnected() && this._isAuthorized) {
                    try {
                        await sendCore();
                        return true;
                    } catch (retryError) {
                        err = retryError;
                    }
                }
            }

            this._log.Error(ExalusConnectionService.ServiceName, String(err));
            return false;
        }
    }

    async RestoreConnectionAsync(): Promise<boolean> {
        if (this._disconnectedOnPurpose)
            return false;

        const version = this._lifecycleVersion;

        if (this._reconnectTask && this._reconnectTaskVersion === version)
            return this._reconnectTask.then(result => result === ConnectionResult.Connected && this._isAuthorized);

        if (this.IsConnected()) {
            const auth = this.GetAuthorizationInfo();
            if (auth !== null)
                return this.authorizeAsyncCore(auth, version);
            return false;
        }

        if (this._restoreTask && this._restoreTaskVersion === version)
            return this._restoreTask;

        let task: Promise<boolean>;
        task = this.restoreConnectionCore(version).finally(() => {
            if (this._restoreTask === task) {
                this._restoreTask = null;
                this._restoreTaskVersion = null;
            }
        });
        this._restoreTask = task;
        this._restoreTaskVersion = version;

        return task;
    }

    private async restoreConnectionCore(version: number): Promise<boolean> {
        const auth = this.GetAuthorizationInfo();
        if (auth === null) {
            DependencyContainer.Log?.Error(
                ExalusConnectionService.ServiceName,
                "Connection restore failed, no controller authorization info"
            );
            return false;
        }

        const start = Date.now();
        const maxTime = 30 * 60 * 1000; // 30 minutes
        let attempt = 0;

        do {
            if (!this.isLifecycleCurrent(version))
                return false;

            if (this.IsConnected()) {
                if (await this.authorizeAsyncCore(auth, version))
                    return true;
            } else {
                const result = await this.connectAndAuthorizeCore(auth, version);
                if (result === ConnectionResult.Connected)
                    return true;
            }

            if (!this.isLifecycleCurrent(version))
                return false;

            const delay = Math.min(30_000, 5_000 * Math.pow(2, attempt++));
            await new Promise(r => setTimeout(r, delay));

            if (!this.isLifecycleCurrent(version))
                return false;

            this._allBrokersChecked = false;
            this._serversBrokerAddress = this._serversBrokerAddressList[0];
        } while (Date.now() - start < maxTime);

        DependencyContainer.Log?.Error(
            ExalusConnectionService.ServiceName,
            "Connection restore failed"
        );
        return false;
    }

    async SendAndWaitForResponseAsync<T>(
        dataFrame: IDataFrame<any>,
        timeout: number,
        useCache: boolean,
        logTransmission = true,
        retryOnUnauthorized = true
    ): Promise<IDataFrame<T>> {
        if (dataFrame.Method === Method.Get && useCache) {
            if (!(await this._controllerConfiguration?.DidCofigurationChangeAsync())) {
                const cached = this._cache?.GetCache(dataFrame) as IDataFrame<T> | undefined;
                if (cached) return cached;
            }
        }

        if (!this.IsConnected()) {
            const restored = await this.RestoreConnectionAsync();
            if (!restored) throw new Error("Failed to restore connection!");
        }

        if (dataFrame.Resource !== "/users/user/login")
            await this._session?.WaitForSessionCreationAsync();

        return new Promise<IDataFrame<T>>(async (resolve, reject) => {
            const timeoutId = window.setTimeout(() => {
                this._dataReceivedEvent.Unsubscribe(onRx);
                this._pendingRequests.delete(dataFrame.TransactionId!);
                reject(new signalR.TimeoutError("Response timeout"));
            }, timeout);

            const onRx = (frame: IDataFrame<any>) => {
                if (frame.TransactionId !== dataFrame.TransactionId)
                    return;

                window.clearTimeout(timeoutId);
                this._dataReceivedEvent.Unsubscribe(onRx);
                this._pendingRequests.delete(dataFrame.TransactionId!);

                if (dataFrame.Method === Method.Get && useCache && frame.Status !== Status.UserIsNotLoggedIn)
                    this._cache?.Cache(frame as DataFrame<any>);

                if (retryOnUnauthorized && frame.Status === Status.UserIsNotLoggedIn) {
                    void (async () => {
                        try {
                            await this._session?.RestoreSessionAsync();
                            resolve(
                                await this.SendAndWaitForResponseAsync<T>(
                                    dataFrame,
                                    timeout,
                                    useCache,
                                    logTransmission,
                                    false
                                )
                            );
                        } catch (error) {
                            reject(error);
                        }
                    })();
                    return;
                }
                resolve(frame as IDataFrame<T>);
            };

            this._dataReceivedEvent.Subscribe(onRx);
            this._pendingRequests.set(dataFrame.TransactionId!, {
                resolve: (v: any) => resolve(v as IDataFrame<T>),
                reject,
                timeoutId,
            });

            if (!(await this.SendAsync(dataFrame, logTransmission))) {
                window.clearTimeout(timeoutId);
                this._dataReceivedEvent.Unsubscribe(onRx);
                this._pendingRequests.delete(dataFrame.TransactionId!);
                reject(new Error("Failed to send request"));
            }
        });
    }

    async SendAndHandleResponseAsync<T>(
        dataFrame: IDataFrame<any>,
        timeout: number,
        dataHandler: (r: IDataFrame<T>) => void,
        logTransmission = true
    ): Promise<void> {
        if (!this.IsConnected()) throw new Error("Connection is not established");
        if (dataFrame.Resource !== "/users/user/login")
            await this._session?.WaitForSessionCreationAsync();

        return new Promise<void>(async (resolve, reject) => {
            const resetTimeout = () =>
                window.setTimeout(() => {
                    cleanup();
                    reject(new signalR.TimeoutError("MultiDataResponse timeout"));
                }, timeout);

            let tid = resetTimeout();
            const cleanup = () => {
                window.clearTimeout(tid);
                this._dataReceivedEvent.Unsubscribe(onRx);
                this._pendingRequests.delete(dataFrame.TransactionId!);
            };

            const onRx = (frame: IDataFrame<any>) => {
                if (frame.TransactionId !== dataFrame.TransactionId) return;
                window.clearTimeout(tid);
                switch (frame.Status) {
                    case Status.MultiDataResponseStart:
                    case Status.MultiDataResponse:
                        dataHandler(frame as IDataFrame<T>);
                        tid = resetTimeout();
                        break;
                    case Status.MultiDataResponseStop:
                    case Status.OK:
                        dataHandler(frame as IDataFrame<T>);
                        cleanup();
                        resolve();
                        break;
                    default:
                        cleanup();
                        reject(new Error(`Unexpected status ${frame.Status}`));
                }
            };

            this._dataReceivedEvent.Subscribe(onRx);
            this._pendingRequests.set(dataFrame.TransactionId!, {
                resolve: () => resolve(),
                reject,
                timeoutId: tid,
            });

            if (!(await this.SendAsync(dataFrame, logTransmission))) {
                cleanup();
                reject(new Error("Failed to send request"));
            }
        });
    }

    async SendAndHandleStreamAsync<T>(
        dataFrame: IDataFrame<any>,
        streamHandler: StreamHandler<T>,
        logTransmission = true
    ): Promise<void> {
        if (!this.IsConnected()) throw new Error("Connection is not established");
        if (dataFrame.Resource !== "/users/user/login")
            await this._session?.WaitForSessionCreationAsync();

        return new Promise<void>(async (resolve, reject) => {
            const timeoutId = window.setTimeout(() => {
                this._streamStartedEvent.Unsubscribe(onStreamStart);
                reject(new signalR.TimeoutError("Stream registration timeout"));
            }, 8_000);

            const onStreamStart = (streamId: string) => {
                if (streamId !== dataFrame.TransactionId) return;
                window.clearTimeout(timeoutId);
                this._streamStartedEvent.Unsubscribe(onStreamStart);

                this._connection!
                    .stream("WatchStream", streamId)
                    .subscribe({
                        next: (item: T) => streamHandler.Next(item),
                        complete: () => {
                            streamHandler.Complete();
                            resolve();
                        },
                        error: (err) => {
                            streamHandler.Error(err);
                            reject(new StreamError(String(err)));
                        },
                    });
            };

            this._streamStartedEvent.Subscribe(onStreamStart);

            if (!(await this.SendAsync(dataFrame, logTransmission))) {
                window.clearTimeout(timeoutId);
                this._streamStartedEvent.Unsubscribe(onStreamStart);
                reject(new Error("Failed to send request"));
            }
        });
    }

    async PingControllerAsync(): Promise<boolean> {
        if (!this.IsConnected() || !this._isAuthorized) return false;
        if (Date.now() - this._lastReceivedPacket < ExalusConnectionService.PING_INTERVAL_MS)
            return false;

        const frame = new DataFrame();
        frame.Resource = "/system/ping";
        frame.Method = Method.Get;

        try {
            await this.SendAndWaitForResponseAsync(frame, 2_000, false, false);
            return true;
        } catch {
            return false;
        }
    }

    OnDataReceivedEvent(): ITypedEvent<any> {
        return this._dataReceivedEvent;
    }
    OnConnectionStateChangedEvent(): ITypedEvent<ConnectionState> {
        return this._connectionStateChangedEvent;
    }
    OnErrorOccuredEvent(): ITypedEvent<[string, string]> {
        return this._errorOccuredEvent;
    }

    private serialisedConnect(version = this._lifecycleVersion): Promise<ConnectionResult> {
        if (!this.isLifecycleCurrent(version))
            return Promise.resolve(ConnectionResult.FailedToConnect);

        if (this._connectTask && this._connectTaskVersion === version)
            return this._connectTask;

        let task: Promise<ConnectionResult>;
        task = this.connectCore(version).finally(() => {
            if (this._connectTask === task) {
                this._connectTask = null;
                this._connectTaskVersion = null;
            }
        });
        this._connectTask = task;
        this._connectTaskVersion = version;
        return task;
    }

    private reconnectAsync(version = this._lifecycleVersion): Promise<ConnectionResult> {
        if (!this.isLifecycleCurrent(version))
            return Promise.resolve(ConnectionResult.FailedToConnect);

        if (this._reconnectTask && this._reconnectTaskVersion === version)
            return this._reconnectTask;

        let task: Promise<ConnectionResult>;
        task = this.reconnectCore(version).finally(() => {
            if (this._reconnectTask === task) {
                this._reconnectTask = null;
                this._reconnectTaskVersion = null;
            }
        });
        this._reconnectTask = task;
        this._reconnectTaskVersion = version;
        return task;
    }

    private async reconnectCore(version: number): Promise<ConnectionResult> {
        if (!this.isLifecycleCurrent(version))
            return ConnectionResult.FailedToConnect;

        // A cloud reconnect is useful only when the serial and PIN are available;
        // a connected SignalR transport is not an authorized controller session.
        if (!this._serialId || !this._PIN)
            return ConnectionResult.FailedToConnect;

        const start = Date.now();
        const maxTime = 30 * 60 * 1000;
        let attempt = 0;

        while (Date.now() - start < maxTime) {
            if (!this.isLifecycleCurrent(version))
                return ConnectionResult.FailedToConnect;

            const connection = this._connection;
            if (connection) {
                // Detach handlers before stopping so this intentional replacement
                // does not look like another unexpected disconnect.
                if (this._connection === connection)
                    this.cleanup();
                try { await connection.stop(); } catch {}
            }

            this._isAuthorized = false;
            const result = await this.serialisedConnect(version);
            if (result === ConnectionResult.Connected && this._isAuthorized)
                return result;

            if (!this.isLifecycleCurrent(version))
                return ConnectionResult.FailedToConnect;

            const delay = Math.min(30_000, 5_000 * Math.pow(2, attempt++));
            await new Promise(resolve => setTimeout(resolve, delay));
        }

        return ConnectionResult.FailedToConnect;
    }

    // Wait until connection reaches any of the target states (or timeout)
    private async waitForState(targets: signalR.HubConnectionState[], timeoutMs = 10_000): Promise<void> {
        const start = Date.now();
        while (true) {
            const st = this._connection?.state;
            if (st == null) return;
            if (st != null && targets.includes(st)) return;
            if (Date.now() - start > timeoutMs) throw new Error(`waitForState timeout; state=${st}`);
            await new Promise(r => setTimeout(r, 50));
        }
    }

    private async connectCore(version: number): Promise<ConnectionResult> {
        if (!this.isLifecycleCurrent(version))
            return ConnectionResult.FailedToConnect;

        if (!this._address)
            return ConnectionResult.ControllerIsNotConnected;

        // Rebuild connection when broker address changes
        if (this._connection && this._currentAddress !== this._address) {
            this._log.Info(ExalusConnectionService.ServiceName, "Broker changed → rebuilding connection");
            const oldConnection = this._connection;
            this.cleanup();
            try { await oldConnection.stop(); } catch {}
        }

        // If something is already happening – wait rather than fighting it
        if (this._connection) {
            const existingConnection = this._connection;
            const s = existingConnection.state;
            if (s === signalR.HubConnectionState.Connected) {
                if (this._serialId && this._PIN && !await this.reauthorizeIfPossible(version)) {
                    this.cleanup();
                    try { await existingConnection.stop(); } catch {}
                    return ConnectionResult.FailedToConnect;
                }
                return ConnectionResult.Connected;
            }
            if (s === signalR.HubConnectionState.Connecting || s === signalR.HubConnectionState.Reconnecting) {
                this._log.Info(ExalusConnectionService.ServiceName, "Existing connection is starting/reconnecting – waiting.");
                try {
                    await this.waitForState([signalR.HubConnectionState.Connected, signalR.HubConnectionState.Disconnected]);
                } catch { /* ignore */ }
                if (!this.isLifecycleCurrent(version))
                    return ConnectionResult.FailedToConnect;
                if (this._connection?.state === signalR.HubConnectionState.Connected) {
                    const connectedConnection = this._connection;
                    if (this._serialId && this._PIN && !await this.reauthorizeIfPossible(version)) {
                        this.cleanup();
                        try { await connectedConnection.stop(); } catch {}
                        return ConnectionResult.FailedToConnect;
                    }
                    return ConnectionResult.Connected;
                }
                if (this._connection === existingConnection) {
                    this.cleanup();
                    try { await existingConnection.stop(); } catch {}
                }
            }
            if (s === signalR.HubConnectionState.Disconnecting) {
                await this.waitForState([signalR.HubConnectionState.Disconnected]).catch(() => {});
            }
        }

        if (!this.isLifecycleCurrent(version))
            return ConnectionResult.FailedToConnect;

        this.initializeServices();
        this.cleanup(); // ensures no dangling timers/handlers and clears _connection

        for (const t of TRANSPORTS) {
            if (!this.isLifecycleCurrent(version))
                return ConnectionResult.FailedToConnect;

            const builder = new signalR.HubConnectionBuilder()
                .withAutomaticReconnect(new FastRetryPolicy())
                .configureLogging(new SignalrLogger())
                // ASP.NET Core 8 stateful reconnect (buffering)
                .withStatefulReconnect({ bufferSize: 256_000 })
                .withServerTimeout(ExalusConnectionService.SERVER_TIMOUT_MS)
                .withKeepAliveInterval(ExalusConnectionService.PING_INTERVAL_MS - 1_000)
                .withUrl(`https://${this._address}/broker`, {
                    // skipNegotiation only for WebSockets
                    skipNegotiation: t === signalR.HttpTransportType.WebSockets,
                    transport: t,
                });

            if (this._log.LogLevel === LogLevel.Debug) {
                builder.configureLogging(signalR.LogLevel.Debug);
            }

            const conn = builder.build();
            this._connection = conn;
            this._currentAddress = this._address;
            this.wireConnectionEvents(conn);

            try {
                await conn.start();
                if (!this.isLifecycleCurrent(version) || this._connection !== conn) {
                    try { await conn.stop(); } catch {}
                    try { (conn as any)?.off?.(); } catch {}
                    return ConnectionResult.FailedToConnect;
                }

                this._everConnected = true;

                // A cloud transport is usable only after AuthorizeTo(serial, PIN).
                if (this._serialId && this._PIN && !await this.reauthorizeIfPossible(version)) {
                    if (this._connection === conn)
                        this.cleanup();
                    try { await conn.stop(); } catch {}
                    this._connectionStateChangedEvent.Invoke(ConnectionState.Failed);
                    return ConnectionResult.FailedToConnect;
                }

                if (!this.isLifecycleCurrent(version) || this._connection !== conn) {
                    if (this._connection === conn)
                        this.cleanup();
                    try { await conn.stop(); } catch {}
                    return ConnectionResult.FailedToConnect;
                }
                this._consecutivePingFailures = 0;
                this.startPingLoop();
                this._connectionStateChangedEvent.Invoke(ConnectionState.Connected);
                return ConnectionResult.Connected;
            } catch (err: any) {
                this._log.Warning(
                    ExalusConnectionService.ServiceName,
                    `Transport ${signalR.HttpTransportType[t]} failed (${err?.message}).`
                );
                // Use local 'conn' because onclose→cleanup may null out this._connection
                if (this._connection === conn)
                    this.cleanup();
                try { await conn.stop(); } catch {}
                try { (conn as any)?.off?.(); } catch {}
                // try next transport
            }
        }

        this._connectionStateChangedEvent.Invoke(ConnectionState.Failed);
        return ConnectionResult.FailedToConnect;
    }

    private waitForReconnection(timeoutMs: number = 0): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            if (!this._connection || this._connection.state !== signalR.HubConnectionState.Reconnecting) {
                reject(new Error("Connection is not in reconnecting state"));
                return;
            }

            let timeoutId: number | undefined = undefined;

            if (timeoutMs > 0) {
                timeoutId = window.setTimeout(() => {
                    this._connectionStateChangedEvent.Unsubscribe(handler);
                    reject(new Error("Reconnection timeout"));
                }, timeoutMs);
            }

            const handler = (state: ConnectionState) => {
                if (state === ConnectionState.Connected) {
                    if (timeoutId) window.clearTimeout(timeoutId);
                    this._connectionStateChangedEvent.Unsubscribe(handler);
                    resolve();
                }
                else if (state === ConnectionState.Disconnected || state === ConnectionState.Failed) {
                    if (timeoutId) window.clearTimeout(timeoutId);
                    this._connectionStateChangedEvent.Unsubscribe(handler);
                    reject(new Error("Connection closed during reconnection"));
                }
            };

            this._connectionStateChangedEvent.Subscribe(handler);
            if (this._connection.state !== signalR.HubConnectionState.Reconnecting) {
                if (timeoutId) window.clearTimeout(timeoutId);
                this._connectionStateChangedEvent.Unsubscribe(handler);

                if (this._connection.state === signalR.HubConnectionState.Connected) {
                    resolve();
                } else {
                    reject(new Error(`Connection is in unexpected state: ${this._connection.state}`));
                }
            }
        });
    }

    private initializeServices(): void {
        this._controllerConfiguration = Api.Get<IControllerConfigurationService>(
            ControllerConfigurationService.ServiceName
        );
        this._cache = Api.Get<IWebApiCacheService>(WebApiCacheService.ServiceName);
        this._session = Api.Get<SessionService>(SessionService.ServiceName);
    }

    private wireConnectionEvents(connection: signalR.HubConnection): void {
        const isCurrentConnection = () => this._connection === connection;

        connection.on("Pong", () => {
            if (!isCurrentConnection()) return;
            this._lastReceivedPacket = Date.now();
            this._pongReceivedEvent.Invoke();
        });

        // Server calls this; make it quiet and useful
        connection.on("controllerdisconnected", () => {
            if (!isCurrentConnection()) return;
            this._isAuthorized = false;
            this._connectionStateChangedEvent.Invoke(ConnectionState.Disconnected);
        });

        connection.on("Authorization", (ok: boolean) => {
            if (isCurrentConnection()) this._authorizationReceivedEvent.Invoke(ok);
        });
        connection.on("Registration", (d: string) => {
            if (isCurrentConnection()) this._registrationReceivedEvent.Invoke(d);
        });
        connection.on("SendError", (s: string, d: string) => {
            if (!isCurrentConnection()) return;
            if (s.startsWith("NotAuthorized:")) {
                if (this._serialId && this._PIN) {
                    void this.AuthorizeAsync(new AuthorizationInfo(this._serialId, this._PIN)).catch(error => {
                        this._log.Warning(ExalusConnectionService.ServiceName, `Re-authorization failed: ${error}`);
                    });
                }
            } else {
                this._errorOccuredEvent.Invoke([s, d]);
            }
        });
        connection.on("Data", (_: string, json: string) => {
            if (!isCurrentConnection()) return;
            this._lastReceivedPacket = Date.now();

            if ((window as any)["packets"] === true)
                this._log.Debug(ExalusConnectionService.ServiceName, `Received: ${JSON.stringify(JSON.parse(json), null, 2)}`);

            this._dataReceivedEvent.Invoke(JSON.parse(json));
        });

        connection.onclose(() => {
            if (!isCurrentConnection()) return;
            this._isAuthorized = false;
            this.cleanup();
            this._connectionStateChangedEvent.Invoke(ConnectionState.Disconnected);

            if (this._everConnected && !this._disconnectedOnPurpose && !this._restoreTask && this._serialId && this._PIN) {
                this._connectionStateChangedEvent.Invoke(ConnectionState.Reconnecting);
                void this.reconnectAsync().catch(error => {
                    this._log.Warning(ExalusConnectionService.ServiceName, `Automatic reconnect failed: ${error}`);
                });
            }
        });

        connection.onreconnecting(() => {
            if (!isCurrentConnection()) return;
            this._isAuthorized = false;
            this._connectionStateChangedEvent.Invoke(ConnectionState.Reconnecting);
        });

        connection.onreconnected(() => {
            void (async () => {
                if (!isCurrentConnection()) return;
                const version = this._lifecycleVersion;
                this._consecutivePingFailures = 0;

                // SignalR restored the transport, but the broker still requires
                // AuthorizeTo(serial, PIN) for the controller session.
                const restored = await this.RestoreConnectionAsync();
                if (!isCurrentConnection() || !this.isLifecycleCurrent(version))
                    return;

                if (restored) {
                    this._connectionStateChangedEvent.Invoke(ConnectionState.Connected);
                    await this._session?.RestoreSessionAsync();
                } else if (!this._disconnectedOnPurpose) {
                    this._connectionStateChangedEvent.Invoke(ConnectionState.Reconnecting);
                    await this.reconnectAsync(version);
                }
            })().catch(error => {
                this._log.Warning(ExalusConnectionService.ServiceName, `Post-reconnect restore failed: ${error}`);
                if (!this._disconnectedOnPurpose)
                    void this.reconnectAsync().catch(() => {});
            });
        });
    }

    private startPingLoop(): void {
        if (this._pingTimerId !== null) clearInterval(this._pingTimerId);
        this._pingTimerId = window.setInterval(
            () => void this.pingOnce(),
            ExalusConnectionService.PING_INTERVAL_MS
        );
        window.addEventListener("online", this._tryReconnect);
        window.addEventListener("offline", this._tryReconnect);
        document.addEventListener("visibilitychange", this._onVisibility, true);
        window.addEventListener("pagehide", this._onHidden, true);
        window.addEventListener("pageshow", this._onVisible, true);
    }

    private isBusy(): boolean {
        const s = this._connection?.state;
        return s === signalR.HubConnectionState.Connecting
            || s === signalR.HubConnectionState.Disconnecting
            || s === signalR.HubConnectionState.Reconnecting;
    }

    private async pingOnce(): Promise<void> {
        if (this._disconnectedOnPurpose || !this.IsConnected() || this.isBusy()) return;
        if (Date.now() - this._lastReceivedPacket < ExalusConnectionService.PING_INTERVAL_MS) return;

        const ok = await this.PingControllerAsync();
        if (ok) {
            this._consecutivePingFailures = 0;
        } else if (++this._consecutivePingFailures >= ExalusConnectionService.MAX_CONSECUTIVE_PING_FAILURES) {
            this._consecutivePingFailures = 0;
            this._log.Warning(ExalusConnectionService.ServiceName, "Ping failed too many times → reconnecting...");
            void this.reconnectAsync().catch(error => {
                this._log.Warning(ExalusConnectionService.ServiceName, `Ping reconnect failed: ${error}`);
            });
        }
    }

    private validateConnectionToController(): Promise<boolean> {
        if (this._connectionValidationTask)
            return this._connectionValidationTask;

        this._connectionValidationTask = this.validateConnectionToControllerCore().finally(() => {
            this._connectionValidationTask = null;
        });

        return this._connectionValidationTask;
    }

    private async validateConnectionToControllerCore(): Promise<boolean> {
        if (this.IsConnected()) {
            const frame = new DataFrame();
            frame.Resource = "/system/ping";
            frame.Method = Method.Get;

            try {
                const res = await this.SendAndWaitForResponseAsync(frame, ExalusConnectionService.SERVER_TIMOUT_MS + 1_000, false, false);
                if (res?.Status !== Status.OK) {
                    this._log.Warning(ExalusConnectionService.ServiceName, `Connection check failed, ${res == null ? "result is null" : `status: ${res.Status}`}`);
                    return false;
                } else if (this._connection?.state === signalR.HubConnectionState.Reconnecting) {
                    this._log.Warning(ExalusConnectionService.ServiceName, "Connection check failed, connection is in reconnecting state");
                    return false;
                } else {
                    this._log.Info(ExalusConnectionService.ServiceName, "Connection still active");
                    return true;
                }
            } catch {
                return false;
            }
        } else {
            this._log.Warning(ExalusConnectionService.ServiceName, "Connection was lost");
            return false;
        }
    }

    private ensureConnectionIsHealthy(): void {
        if (this._disconnectedOnPurpose || this._connectTask || this._reconnectTask || this._restoreTask || this.isBusy() || !navigator.onLine)
            return;

        void this.validateConnectionToController().then((isConnected) => {
            if (!isConnected) {
                this._log.Debug(ExalusConnectionService.ServiceName, "Connection lost -> reconnecting...");
                void this.reconnectAsync().catch(error => {
                    this._log.Warning(ExalusConnectionService.ServiceName, `Health reconnect failed: ${error}`);
                });
            }
        });
    }

    private _tryReconnect = (): void => {
        this.ensureConnectionIsHealthy();
    };

    private _onVisibility = (): void => {
        if (document.visibilityState === "visible") {
            this._log.Debug(ExalusConnectionService.ServiceName, "Page visible → connection check");
            this.ensureConnectionIsHealthy();
        }
    };
    private _onHidden = (): void => { /* could pause ping loop if needed */ };
    private _onVisible = (): void => {
        this.ensureConnectionIsHealthy();
    };

    private cleanup(): void {
        if (this._pingTimerId !== null) {
            clearInterval(this._pingTimerId);
            this._pingTimerId = null;
        }

        // reject all outstanding waiters
        for (const [, p] of this._pendingRequests) {
            window.clearTimeout(p.timeoutId);
            p.reject(new Error("Connection lost"));
        }
        this._pendingRequests.clear();

        window.removeEventListener("online", this._tryReconnect);
        window.removeEventListener("offline", this._tryReconnect);
        document.removeEventListener("visibilitychange", this._onVisibility, true);
        window.removeEventListener("pagehide", this._onHidden, true);
        window.removeEventListener("pageshow", this._onVisible, true);

        try { (this._connection as any)?.off?.(); } catch {}
        this._isAuthorized = false;
        this._connection = undefined;
        this._currentAddress = undefined;
    }
}
